/**
 * GrandTransitionOverlay.tsx — R8 #6
 *
 * 1.5s era-themed bloom flourish that plays after the post-cutscene dialogue
 * chain completes, bridging cutscene → main board view. Auto-dismisses after
 * the animation ends by calling dismissGrandTransition().
 *
 * Era palette mapping:
 *   Era 1 (Industrial Dawn)   — orange-amber bloom  (#e8a050)
 *   Era 2 (Oil Rush)          — petroleum-yellow    (#c9974a)
 *   Era 3 (Nuclear)           — chrome-blue         (#a0c0e0)
 *   Era 4 (Sustainable Future)— solar-gold          (#ffd86b)
 *
 * VISIBLE WHEN: grandTransitionActive !== null
 * TEST PATH:
 *   After PostCutsceneDialogue completes (P4 speaks) → this overlay appears
 *   for 1.5s, then disappears. Board view is now fully visible beneath it.
 */

import type React from "react";
import { useEffect, useRef } from "react";
import { ERA_NAMES } from "../../store/game-store.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./GrandTransitionOverlay.module.css";

const TRANSITION_DURATION_MS = 1500;

// Era palette: color, glow, bg overlay.
interface EraPalette {
  color: string;
  glow: string;
  bg: string;
  icon: string;
}

const ERA_PALETTES: Record<1 | 2 | 3 | 4, EraPalette> = {
  1: {
    color: "#e8a050",
    glow: "rgba(232, 160, 80, 0.45)",
    bg: "radial-gradient(ellipse at 50% 40%, rgba(232, 160, 80, 0.35) 0%, rgba(45, 20, 8, 0.92) 70%)",
    icon: "🏭",
  },
  2: {
    color: "#c9974a",
    glow: "rgba(201, 151, 74, 0.4)",
    bg: "radial-gradient(ellipse at 50% 40%, rgba(201, 151, 74, 0.35) 0%, rgba(30, 18, 5, 0.92) 70%)",
    icon: "🛢️",
  },
  3: {
    color: "#a0c0e0",
    glow: "rgba(160, 192, 224, 0.4)",
    bg: "radial-gradient(ellipse at 50% 40%, rgba(160, 192, 224, 0.30) 0%, rgba(15, 22, 35, 0.92) 70%)",
    icon: "⚛️",
  },
  4: {
    color: "#ffd86b",
    glow: "rgba(255, 216, 107, 0.45)",
    bg: "radial-gradient(ellipse at 50% 40%, rgba(255, 216, 107, 0.38) 0%, rgba(18, 28, 15, 0.92) 70%)",
    icon: "☀️",
  },
};

// Static particle positions (pre-computed so SSR / test is stable).
const PARTICLES: ReadonlyArray<{ x: string; y: string; dur: string; delay: string }> = [
  { x: "20%", y: "60%", dur: "1.1s", delay: "0.1s" },
  { x: "35%", y: "70%", dur: "1.3s", delay: "0.2s" },
  { x: "50%", y: "65%", dur: "1.0s", delay: "0.0s" },
  { x: "65%", y: "72%", dur: "1.2s", delay: "0.15s" },
  { x: "80%", y: "62%", dur: "0.9s", delay: "0.3s" },
  { x: "28%", y: "55%", dur: "1.4s", delay: "0.05s" },
  { x: "72%", y: "58%", dur: "1.1s", delay: "0.25s" },
  { x: "45%", y: "80%", dur: "1.3s", delay: "0.1s" },
];

export function GrandTransitionOverlay(): React.ReactElement | null {
  const grandTransitionActive = useGameStore((s) => s.grandTransitionActive);
  const dismissGrandTransition = useGameStore((s) => s.dismissGrandTransition);

  // Auto-dismiss after animation ends.
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (grandTransitionActive === null) return;

    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      dismissGrandTransition();
    }, TRANSITION_DURATION_MS);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
    };
  }, [grandTransitionActive, dismissGrandTransition]);

  if (grandTransitionActive === null) return null;

  const era = grandTransitionActive.era;
  const palette = ERA_PALETTES[era];
  const eraName = ERA_NAMES[era - 1] ?? "New Era";

  return (
    <dialog
      open
      className={styles.overlay}
      data-testid="grand-transition-overlay"
      aria-label={`Entering ${eraName}`}
      style={
        {
          "--era-color": palette.color,
          "--era-glow": palette.glow,
        } as React.CSSProperties
      }
    >
      {/* Bloom background */}
      <div className={styles.bloom} style={{ background: palette.bg }} aria-hidden="true" />

      {/* Particles */}
      {PARTICLES.map((p) => (
        <span
          key={`${p.x}-${p.y}`}
          className={styles.particle}
          aria-hidden="true"
          style={
            {
              left: p.x,
              top: p.y,
              "--dur": p.dur,
              "--delay": p.delay,
            } as React.CSSProperties
          }
        />
      ))}

      {/* Era label */}
      <div className={styles.eraLabel}>
        <span className={styles.eraIcon} aria-hidden="true">
          {palette.icon}
        </span>
        <div className={styles.eraTitle}>Era {era}</div>
        <div className={styles.eraSubtitle}>{eraName}</div>
      </div>
    </dialog>
  );
}
